import { IonButton, IonSpinner } from '@ionic/react';
import { isPlatform } from '../utils/isPlatform';
import { useState } from 'react';
import { Share as CapacitorShare } from '@capacitor/share';
import { createUseStyles } from 'react-jss';
import { TabRadioButtons } from '../components/TabRadioButtons';
import { CopyTextButton } from '../components/CopyTextButton';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { DevSettings } from '../support/DevSettings';
import { AlertPresenter } from '../AlertBinder';
import { UserFacingError } from '../innerhive-plus/revenue-cat/UserFacingError';
import { useCanShare } from '../utils/useCanShare';
import { Href } from '../components/Href';

const useStyles = createUseStyles({
    linkButton: {
        height: 40
    }
});

export interface ILinkInfo {
    linkDurationDays: number | undefined;
};

export interface IShareLinkButtonProps {
    'data-type': string;

    buttonLabel: string;
    buttonLabelWhenNativeShare: string;

    // this works with android/gmail and ios/mail
    // it does nothing for other shares, including ios/gmail
    shareSubject: string;
    shareBody: string;

    createLinkUrl: (linkInfo: ILinkInfo) => Promise<string>;

    asLink?: boolean;
    hasDuration?: boolean;
    disabled?: boolean;
    getDurationMessage?: (linkDurationDays: number) => any;
    onMobileSharingOpened?: (linkInfo: ILinkInfo) => void;
    onMobileSharingDone?: () => void;
    onLinkCopied?: (linkInfo: ILinkInfo) => void;
}

export const ShareLinkButton: React.FC<IShareLinkButtonProps> = ({
    'data-type': dataType,
    buttonLabel,
    buttonLabelWhenNativeShare,
    shareSubject,
    shareBody,
    asLink,
    hasDuration,
    disabled,
    getDurationMessage,
    createLinkUrl,
    onMobileSharingOpened = () => {},
    onMobileSharingDone = () => {},
    onLinkCopied = () => {}
}) => {
    const classes = useStyles();

    const [viewerUrl, setViewerUrl] = useState<string>('');

    const alert = useInstance(AlertPresenter);

    const devSettings = useInstance(DevSettings);

    const [isCreating, setIsCreating] = useState(false);

    const canShare = useCanShare();

    const [linkDurationDays, setLinkDurationDays] = useState<number | undefined>(hasDuration ? 1 : undefined);

    const shareLink = async () => {
        let url = viewerUrl;
        if (!url) {
            setIsCreating(true);
            try {
                url = await createLinkUrl({ linkDurationDays });
                setViewerUrl(url);
            }
            catch (err: any){
                if (err && err.errorCode === 'unknown.invalid_format'){
                    // then it must be email because that is the only validation that throws this
                    alert.showAndLogError(new UserFacingError({
                        status: 400,
                        errorCode: 'email.invalid',
                        displayMessage: 'Please enter a valid email.',
                        message: err.message || ''
                    }));
                }
                else {
                    alert.showAndLogError(err);
                }

            }
            finally {
                setIsCreating(false);
            }
        }

        if (url && canShare) {
            onMobileSharingOpened({ linkDurationDays });

            try {
                const shareInfo = {
                    url,
                    title: shareSubject,
                    text: shareBody
                };
                if (isPlatform('capacitor')) {
                    await CapacitorShare.share(shareInfo);
                } else {
                    await navigator.share(shareInfo);
                }

                onMobileSharingDone();
            } catch (err: any) {
                // if the user closes the share sheet without sharing, we don't care
                // (in web it's "AbortError", in native it's "share canceled")
                if (err?.name !== 'AbortError' && !err?.toString().toLowerCase().includes('share canceled')) {
                    throw err;
                }
            }
        }
    };

    if (canShare || !viewerUrl){
        return (
            <div data-type={dataType}>
                {hasDuration &&
                    <div style={{padding: '0 2px 2px 2px'}}>
                        <TabRadioButtons
                            tighterXs
                            value={linkDurationDays}
                            onChange={v => setLinkDurationDays(v)}
                            dataTypePrefix="day"
                            buttons={[
                                ...(devSettings.showVeryShortExpiration ? [{
                                    label: '1 Min',
                                    value: .0007
                                }] : []),
                                {
                                    label: '1 Day',
                                    value: 1
                                },
                                {
                                    label: '7 Days',
                                    value: 7
                                },
                                {
                                    label: '30 Days',
                                    value: 30
                                }
                            ]}
                        />
                    </div>
                }

                {asLink ? (
                    <Href onClick={shareLink}>
                        {canShare ? buttonLabelWhenNativeShare : buttonLabel}
                    </Href>
                ) : (
                    <IonButton
                        fill="outline"
                        expand="block"
                        disabled={isCreating || disabled}
                        className={classes.linkButton}
                        onClick={shareLink}
                        data-type="link-button"
                    >
                        {isCreating ? <IonSpinner name="dots" /> : <>{canShare ? buttonLabelWhenNativeShare : buttonLabel}</>}
                    </IonButton>
                )}

                {canShare && hasDuration && getDurationMessage && <p style={{marginTop: 8}}>{getDurationMessage(linkDurationDays!)}</p>}
            </div>
        );
    } else {
        return (
            <div data-type={dataType}>
                {hasDuration && getDurationMessage &&
                    <p style={{marginTop: 25, marginBottom: 16}}>{getDurationMessage(linkDurationDays!)}</p>
                }

                <CopyTextButton
                    text={viewerUrl}
                    data-type="link-url"
                    onTextCopied={() => onLinkCopied({ linkDurationDays })}
                />
            </div>
        );
    }
};
